import type { IceServerConfig } from '../../../core/model';

export interface ResolutionPreset {
  id: string;
  label: string;
  width: number;
  height: number;
}

export const RESOLUTION_PRESETS: readonly ResolutionPreset[] = [
  { id: '720p', label: '1280 × 720 (720p)', width: 1280, height: 720 },
  { id: '1080p', label: '1920 × 1080 (1080p)', width: 1920, height: 1080 },
  { id: '1440p', label: '2560 × 1440 (1440p)', width: 2560, height: 1440 },
];

export const FPS_PRESETS: readonly number[] = [24, 25, 30, 50, 60];

export function matchResolutionPreset(width: number, height: number): string {
  return RESOLUTION_PRESETS.find((p) => p.width === width && p.height === height)?.id ?? 'custom';
}

/** Canvas dimensions must be positive even integers within what encoders accept. */
export function sanitizeDimension(value: number): number {
  const rounded = Math.round(Number.isFinite(value) ? value : 0);
  const even = rounded - (rounded % 2);
  return Math.min(7680, Math.max(16, even));
}

export function sanitizeFps(value: number): number {
  return Math.min(120, Math.max(1, Math.round(Number.isFinite(value) ? value : 30)));
}

/** "a, b" -> ['a', 'b']; a single URL stays a string, matching RTCIceServer.urls. */
export function parseIceUrls(text: string): string | string[] {
  const urls = text
    .split(/[\s,]+/)
    .map((u) => u.trim())
    .filter(Boolean);
  return urls.length === 1 ? urls[0]! : urls;
}

export function formatIceUrls(urls: string | string[]): string {
  return Array.isArray(urls) ? urls.join(', ') : urls;
}

/** Problem with an ICE server entry, or null when it is usable. */
export function iceServerProblem(server: IceServerConfig): string | null {
  const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
  if (urls.length === 0 || urls.every((u) => u === '')) return 'Enter at least one URL.';
  for (const url of urls) {
    if (!/^(stun|stuns|turn|turns):[^\s]+$/i.test(url)) return `"${url}" must start with stun:, stuns:, turn: or turns:`;
  }
  const isTurn = urls.some((u) => /^turns?:/i.test(u));
  if (isTurn && (!server.username || !server.credential)) return 'TURN servers need a username and a credential.';
  return null;
}

/** Drops empty optional fields so the stored config stays minimal. */
export function cleanIceServer(server: IceServerConfig): IceServerConfig {
  const out: IceServerConfig = { urls: server.urls };
  if (server.username) out.username = server.username;
  if (server.credential) out.credential = server.credential;
  return out;
}

export function urlProblem(value: string, schemes: readonly string[]): string | null {
  if (value.trim() === '') return null;
  try {
    const url = new URL(value);
    return schemes.includes(url.protocol) ? null : `Must start with ${schemes.map((s) => `${s}//`).join(' or ')}`;
  } catch {
    return 'Not a valid URL.';
  }
}
